import { useState, type DragEvent } from 'react';
import {
  OPEN_DEAL_STAGES,
  PASS_REASONS,
  intelDealBoard,
  intelDealGate,
  intelDealPass,
  intelDealReopen,
  intelDealStage,
  type DealBoard,
  type DealBoardRow,
  type DealStage,
  type DealStageDef,
  type DealWriteResult,
  type FinancialProspect,
  type PassReason,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { AboutButton } from '../../ui/AboutThis';
import { ErrorState, Skeleton } from '../../ui/states';
import { DASH, type TabProps } from './common';
import { GC, M, type Deal, type Grade } from './model';
import './deals/deals.css';
import { useBuildText } from '../../ui/buildLabels';

const CAPS = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const OUT = 'height:26px;display:inline-flex;align-items:center;padding:0 9px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;border:1px solid rgba(47,92,151,.35);color:#2F5C97;';
const DAY = 86400000;

/** A card on the board: the prospect, its deal card when the model can run on it, its board row. */
export interface BoardCard {
  p: FinancialProspect;
  d: Deal | null;
  row: DealBoardRow;
}

/** Whole days since an ISO time, at `now`. */
export const daysSince = (iso: string | null, now: number) => (iso ? Math.max(0, Math.floor((now - Date.parse(iso)) / DAY)) : 0);

/** "Sep 30" (a date only, read as the calendar day it names). */
export const shortDate = (iso: string, utc = false) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', ...(utc ? { timeZone: 'UTC' } : {}) });

/** The move log's time: the clock for a move made today, the date otherwise. */
export function moveTime(iso: string, now: number): string {
  const d = new Date(iso);
  const n = new Date(now);
  if (d.toDateString() === n.toDateString()) return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return shortDate(iso);
}

/** Merge one write's answer into the board: the deal row, and its move at the top of the log. */
export function applyWrite(b: DealBoard, r: DealWriteResult): DealBoard {
  return { ...b, deals: b.deals.map((x) => (x.prospectId === r.deal.prospectId ? r.deal : x)), moves: [r.move, ...b.moves].slice(0, 12) };
}

/**
 * Tracking (V4 aiIsTrk, T15604; aiNsVals L30643, rebuilt 2026-10-02): the acquisition board.
 * Stages follow how facilities are bought (Sourced -> Closing; Closed joins Portfolio; Pass is
 * off the board with a reason and can be reopened where it left). Each stage has exit checks;
 * Advance needs every check ticked, a drag moves anyway and is logged as a deliberate
 * override. Stage odds are assumed (MODELED) until we have our own closed deals. Everything is
 * read from and written to GET /prospects/board and the stage writes (src/financial/deal-board.ts),
 * each sealed to the Truth Ledger.
 */
export function TrackingTab(p: TabProps) {
  const bt = useBuildText();
  const board = useQuery<DealBoard>((signal) => intelDealBoard(p.client, { signal }), [p.client]);
  const [over, setOver] = useState<string | null>(null);
  const [passing, setPassing] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (board.error && !board.data) return <ErrorState error={board.error} onRetry={board.reload} />;
  if (!board.data) return <Skeleton rows={6} label="Loading the acquisition board" />;

  const b = board.data;
  const now = Date.now();
  const defs: DealStageDef[] = b.stages.length ? b.stages : [];
  const def = (s: string) => defs.find((x) => x.key === s);
  const byRow = new Map(b.deals.map((r) => [r.prospectId, r]));
  const byDeal = new Map(p.deals.map((d) => [d.id, d]));
  const cards: BoardCard[] = (p.book.data?.prospects ?? []).flatMap((x) => {
    const row = byRow.get(x.id);
    return row ? [{ p: x, d: byDeal.get(x.id) ?? null, row }] : [];
  });
  const days = (c: BoardCard) => daysSince(c.row.stageEnteredAt, now);
  const entry = (c: BoardCard) => (c.d ? c.d.entry : 0);
  const owner = b.owner ?? DASH;

  const write = (call: Promise<DealWriteResult>, after?: (r: DealWriteResult) => void) => {
    if (p.readOnly) return;
    setErr(null);
    call.then(
      (r) => {
        board.setData((prev) => (prev ? applyWrite(prev, r) : prev));
        after?.(r);
      },
      (e: Error) => setErr(e.message),
    );
  };
  const moveTo = (c: BoardCard, to: DealStage, override: boolean) => {
    if (c.row.stage === to) return;
    setPassing(null);
    write(intelDealStage(p.client, { params: { id: c.p.id }, body: { to, ...(override ? { override: true } : {}) } }), (r) => {
      if (r.deal.stage === 'Closed') p.book.reload();
    });
  };
  const nextOf = (s: DealStage): DealStage => (OPEN_DEAL_STAGES[OPEN_DEAL_STAGES.indexOf(s) + 1] ?? 'Closed');
  const gatesOf = (c: BoardCard, s: DealStage) => {
    const n = def(s)?.gates.length ?? 0;
    const got = c.row.gates[s] ?? [];
    return Array.from({ length: n }, (_, i) => got[i] === true);
  };
  const onDrop = (col: DealStage) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const c = cards.find((x) => x.p.id === id);
    if (!c) return;
    const g = gatesOf(c, c.row.stage);
    const ready = g.length > 0 && g.every(Boolean);
    moveTo(c, col, !(ready && nextOf(c.row.stage) === col));
  };

  const open = cards.filter((c) => (OPEN_DEAL_STAGES as string[]).includes(c.row.stage));
  const stalled = open.filter((c) => days(c) >= (def(c.row.stage)?.limitDays ?? Infinity));
  const closed = cards.filter((c) => c.row.stage === 'Closed');
  const passed = cards.filter((c) => c.row.stage === 'Pass');
  const stats: [string, string, string][] = [
    ['Open pipeline', M(open.reduce((s, c) => s + entry(c), 0)), open.length + ' deals · ' + open.reduce((s, c) => s + (c.d?.beds ?? 0), 0) + ' beds'],
    ['Weighted pipeline', M(open.reduce((s, c) => s + entry(c) * (def(c.row.stage)?.odds ?? 0), 0)), bt('assumed odds · MODELED')],
    ['Avg days in stage', open.length ? Math.round(open.reduce((s, c) => s + days(c), 0) / open.length) + 'd' : DASH, 'open deals only'],
    ['Stalled', String(stalled.length), stalled.length ? stalled.map((c) => c.p.name.split(' ')[0]).join(', ') : 'none past the stage limit'],
    ['Closed', String(closed.length), M(closed.reduce((s, c) => s + entry(c), 0)) + ' deployed'],
  ];
  const ro = p.readOnly;

  return (
    <div data-testid="intel-tracking" style={sx('display:flex;flex-direction:column;gap:9px;min-width:0;')}>
      <div>
        {/* V9: the explainer opens on the "i" (design k17028). */}
        <div style={sx('font-size:19px;font-weight:700;letter-spacing:-.018em;')}>
          Acquisition tracking
          <AboutButton open={!!p.st.v9i?.track} onToggle={() => p.set({ v9i: { ...p.st.v9i, track: !p.st.v9i?.track } })} gap={6} />
        </div>
        {p.st.v9i?.track && <div style={sx('font-size:16px;color:#54606C;')}>Each stage has exit checks. Tick them on the card, then move it on. Every move and check is logged to the Truth Ledger. Stage odds are assumed until we have our own closed deals.</div>}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(168px,1fr));gap:11px;')}>
        {stats.map(([l, v, n]) => (
          <div key={l} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:8px 2px;')}>
            <div style={sx(CAPS + 'margin-bottom:4px;')}>{l}</div>
            <div style={sx('font-size:20px;font-weight:700;letter-spacing:-.02em;')}>{v}</div>
            <div style={sx('font-size:14px;color:#54606C;')}>{n}</div>
          </div>
        ))}
      </div>
      <div style={sx('overflow-x:auto;')}>
        <div style={sx('display:grid;grid-template-columns:repeat(7,minmax(150px,1fr));gap:8px;align-items:start;min-width:1100px;')}>
          {defs.map((sd, ci) => {
            const col = sd.key;
            const here = cards.filter((c) => c.row.stage === col);
            const val = here.reduce((s, c) => s + entry(c), 0);
            const isOver = over === col;
            return (
              <div
                key={col}
                data-stage={col}
                onDragOver={(e) => {
                  if (ro) return;
                  e.preventDefault();
                  if (over !== col) setOver(col);
                }}
                onDragLeave={() => {
                  if (over === col) setOver(null);
                }}
                onDrop={onDrop(col)}
                style={sx(
                  'box-sizing:border-box;border-radius:11px;padding:9px;min-height:110px;display:flex;flex-direction:column;gap:7px;transition:background .15s,border-color .15s;' +
                    (isOver ? 'background:rgba(79,138,214,.12);border:1px dashed #2F5C97;' : 'background:rgba(34,39,46,.035);border:1px solid rgba(34,39,46,.1);'),
                )}
              >
                <div style={sx('display:flex;align-items:baseline;gap:6px;')}>
                  <span style={sx('font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#3C454F;font-weight:700;')}>{col}</span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;background:rgba(34,39,46,.08);border-radius:4px;padding:1px 6px;')}>{String(here.length)}</span>
                  <span style={sx('flex:1;')} />
                  <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;')}>{here.length && val ? M(val) : ''}</span>
                </div>
                <div title="Assumed odds until we have our own closed deals" style={sx('font-size:14px;color:#54606C;margin-top:-4px;')}>
                  {Math.round(sd.odds * 100) + '% odds · ' + sd.limitDays + 'd limit'}
                </div>
                {here.map((c) => {
                  const d = days(c);
                  const isStalled = d >= sd.limitDays;
                  const g = gatesOf(c, col);
                  const done = g.filter(Boolean).length;
                  const ready = done === g.length;
                  const nxt = defs[ci + 1]?.key ?? 'Closed';
                  const firstOpen = sd.gates[g.indexOf(false)];
                  const isPassing = passing === c.p.id;
                  const grade = (c.d?.grade ?? c.p.grade ?? '') as Grade | '';
                  const contact = c.row.lastContactAt ? 'Contact ' + shortDate(c.row.lastContactAt) : 'No contact logged';
                  return (
                    <div
                      key={c.p.id}
                      data-testid="trk-card"
                      draggable={!ro}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', c.p.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      style={sx(
                        'box-sizing:border-box;background:#FCFAF4;border:1px solid ' +
                          (isStalled ? 'rgba(179,38,30,.45)' : 'rgba(34,39,46,.12)') +
                          ';border-radius:9px;padding:9px 10px;cursor:grab;display:flex;flex-direction:column;gap:5px;',
                      )}
                    >
                      <div style={sx('display:flex;align-items:flex-start;gap:7px;')}>
                        <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:20px;height:22px;flex:0 0 20px;border-radius:5px;font-size:14px;font-weight:700;color:#fff;background:' + (grade && GC[grade] ? GC[grade] : '#5B6B7C') + ';')}>{grade || DASH}</span>
                        <span style={sx('flex:1;min-width:0;font-size:16px;font-weight:700;line-height:1.3;')}>{c.p.name}</span>
                      </div>
                      <div style={sx('font-size:14px;color:#54606C;')}>{c.d ? c.d.city + ', ' + c.d.stt + ' · ' + c.d.beds + ' beds' : (c.p.state ?? DASH)}</div>
                      <div style={sx('display:flex;align-items:baseline;gap:6px;white-space:nowrap;')}>
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;')}>{c.d ? M(c.d.entry) : DASH}</span>
                        <span style={sx('font-size:14px;color:#1F6B52;font-weight:600;')}>{c.d ? (c.d.irr * 100).toFixed(0) + '% IRR' : ''}</span>
                        <span style={sx('flex:1;')} />
                        <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;font-weight:' + (isStalled ? 700 : 500) + ';color:' + (isStalled ? '#B3261E' : '#54606C') + ';')}>{d + 'd'}</span>
                      </div>
                      <div style={sx('display:flex;align-items:center;gap:7px;border-top:1px solid rgba(34,39,46,.08);padding-top:6px;')}>
                        <span style={sx('flex:1;height:4px;border-radius:2px;background:rgba(34,39,46,.08);')}>
                          <span style={sx('display:block;height:100%;border-radius:2px;background:' + (ready ? '#2F8A6F' : '#2F5C97') + ';width:' + (g.length ? (done / g.length) * 100 : 0) + '%;')} />
                        </span>
                        <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{done + ' of ' + g.length + ' checks'}</span>
                      </div>
                      {sd.gates.map((t, i) => (
                        <a
                          key={t}
                          href="#"
                          role="checkbox"
                          aria-checked={g[i]}
                          className="intel-h-gate"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            write(intelDealGate(p.client, { params: { id: c.p.id }, body: { stage: col, index: i, done: !g[i] } }));
                          }}
                          style={sx('display:flex;align-items:flex-start;gap:6px;text-decoration:none;padding:1px 0;')}
                        >
                          <span style={sx('flex:0 0 14px;width:14px;height:22px;border-radius:3px;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#fff;' + (g[i] ? 'background:#2F8A6F;border:1px solid #2F8A6F;' : 'background:#fff;border:1px solid rgba(34,39,46,.35);'))}>✓</span>
                          <span style={sx('font-size:14px;line-height:1.3;color:' + (g[i] ? '#54606C' : '#22272E') + ';' + (g[i] ? 'text-decoration:line-through;' : ''))}>{t}</span>
                        </a>
                      ))}
                      <div style={sx('font-size:14px;color:#3C454F;line-height:1.35;')}>
                        <span style={sx('font-weight:700;')}>Next:</span> <span>{ready ? 'Ready for ' + nxt : firstOpen}</span>
                      </div>
                      <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>
                        <span>{owner}</span> · <span>{contact + (c.row.targetClose ? ' · close ' + shortDate(c.row.targetClose, true) : '')}</span>
                      </div>
                      {!isPassing && (
                        <div style={sx('display:flex;gap:4px;flex-wrap:wrap;align-items:center;')}>
                          {ready ? (
                            <a
                              href="#"
                              title={'Move to ' + nxt}
                              className="intel-h-solid"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                moveTo(c, nxt, false);
                              }}
                              style={sx('height:26px;display:inline-flex;align-items:center;padding:0 7px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;')}
                            >
                              {(nxt === 'Closed' ? 'Close deal' : nxt) + ' →'}
                            </a>
                          ) : (
                            <span title="Finish the checks to move on" style={sx('height:26px;display:inline-flex;align-items:center;padding:0 7px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:rgba(34,39,46,.06);color:#54606C;border:1px solid rgba(34,39,46,.14);cursor:not-allowed;')}>
                              {(nxt === 'Closed' ? 'Close deal' : nxt) + ' →'}
                            </span>
                          )}
                          <a
                            href="#"
                            className="intel-h-out"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (c.d) p.go('target', { aiSel: p.deals.indexOf(c.d), aiLastGroup: 'facility' });
                            }}
                            style={sx('height:26px;display:inline-flex;align-items:center;padding:0 7px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;border:1px solid rgba(47,92,151,.35);color:#2F5C97;')}
                          >
                            Analysis
                          </a>
                          <a
                            href="#"
                            className="intel-h-pass"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!ro) setPassing(c.p.id);
                            }}
                            aria-disabled={ro || undefined}
                            style={sx('height:26px;display:inline-flex;align-items:center;padding:0 4px;font-size:14px;font-weight:600;text-decoration:underline;text-underline-offset:2px;white-space:nowrap;color:#3C454F;' + (ro ? 'opacity:.55;cursor:not-allowed;' : ''))}
                          >
                            Pass
                          </a>
                        </div>
                      )}
                      {isPassing && (
                        <div style={sx('display:flex;flex-direction:column;gap:4px;border-top:1px solid rgba(34,39,46,.08);padding-top:5px;')}>
                          <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>Why pass?</span>
                          <div style={sx('display:flex;gap:4px;flex-wrap:wrap;')}>
                            {PASS_REASONS.map((w) => (
                              <a
                                key={w}
                                href="#"
                                className="intel-h-why"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPassing(null);
                                  write(intelDealPass(p.client, { params: { id: c.p.id }, body: { reason: w as PassReason } }));
                                }}
                                style={sx('height:26px;display:inline-flex;align-items:center;padding:0 9px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;height:24px;font-size:14px;color:#3C454F;border:1px solid rgba(34,39,46,.2);')}
                              >
                                {w}
                              </a>
                            ))}
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPassing(null);
                              }}
                              style={sx('height:26px;display:inline-flex;align-items:center;padding:0 9px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;height:24px;font-size:14px;color:#54606C;')}
                            >
                              Cancel
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {here.length === 0 && (
                  <div style={sx('box-sizing:border-box;border:1px dashed rgba(34,39,46,.2);border-radius:9px;padding:9px 10px;font-size:14px;color:#54606C;line-height:1.4;')}>No deal at this stage. Drop a card here.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {err && <div role="alert" style={sx('font-size:16px;color:#B3261E;')}>{err}</div>}
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:7px 2px;border-top:1px solid rgba(34,39,46,.14);')}>
        <span style={sx(CAPS)}>Closed</span>
        <span style={sx('color:#22272E;font-weight:600;')}>{closed.length + ' closed · ' + M(closed.reduce((s, c) => s + entry(c), 0)) + ' · ' + closed.map((c) => c.p.name.split(' · ')[0]).join(', ')}</span>
        <a href="#" className="intel-h-out" onClick={(e) => { e.preventDefault(); p.go('portfolio'); }} style={sx(OUT)}>Portfolio →</a>
        <span style={sx('flex:1;')} />
        <span style={sx(CAPS)}>Passed</span>
        {passed.length === 0 && <span style={sx('color:#54606C;')}>none yet</span>}
        {passed.map((c) => (
          <span key={c.p.id} style={sx('display:inline-flex;align-items:center;gap:6px;')}>
            <span style={sx('font-weight:600;')}>{c.p.name}</span>
            <span style={sx('color:#54606C;')}>{'at ' + (c.row.passFrom ?? DASH) + ' · ' + (c.row.passReason ?? DASH)}</span>
            <a href="#" onClick={(e) => { e.preventDefault(); write(intelDealReopen(p.client, { params: { id: c.p.id }, body: {} })); }} style={sx('color:#2F5C97;font-weight:600;text-decoration:none;')}>Reopen</a>
          </span>
        ))}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:10px 2px;min-width:0;')}>
          <div style={sx(CAPS + 'margin-bottom:5px;')}>Stalled · past the stage limit</div>
          {stalled.length === 0 && <div style={sx('font-size:16px;color:#1F6B52;padding:6px 0;')}>Every open deal is inside its stage limit.</div>}
          {stalled.map((c) => (
            <div key={c.p.id} style={sx('display:flex;align-items:center;gap:10px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
              <span style={sx('width:8px;height:8px;border-radius:50%;background:#B3261E;flex:0 0 8px;')} />
              <span style={sx('font-size:16px;font-weight:700;')}>{c.p.name}</span>
              <span style={sx('font-size:16px;color:#3C454F;flex:1;min-width:0;')}>{c.row.stage + ' for ' + days(c) + ' days, limit ' + (def(c.row.stage)?.limitDays ?? DASH)}</span>
              <a href="#" className="intel-h-out" onClick={(e) => { e.preventDefault(); if (c.d) p.go('target', { aiSel: p.deals.indexOf(c.d), aiLastGroup: 'facility' }); }} style={sx(OUT)}>Analysis →</a>
            </div>
          ))}
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:10px 2px;min-width:0;')}>
          <div style={sx(CAPS + 'margin-bottom:5px;')}>Time in stage · average vs limit</div>
          <div style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:14px;')}>
            {defs.map((sd) => {
              const xs = cards.filter((c) => c.row.stage === sd.key);
              const avg = xs.length ? xs.reduce((s, c) => s + days(c), 0) / xs.length : 0;
              const mx = 70;
              const isOver = xs.length > 0 && avg >= sd.limitDays;
              return (
                <div key={sd.key} style={sx('display:grid;grid-template-columns:62px minmax(0,1fr) 30px 28px;gap:7px;align-items:center;padding:3px 0;')}>
                  <span style={sx('font-size:14px;color:#3C454F;')}>{sd.key}</span>
                  <span style={sx('position:relative;height:8px;background:rgba(34,39,46,.06);border-radius:3px;')}>
                    <span style={sx('position:absolute;left:0;top:0;bottom:0;border-radius:3px;background:' + (isOver ? '#B3261E' : '#2F5C97') + ';width:' + Math.min(100, (avg / mx) * 100).toFixed(1) + '%;')} />
                    <span style={sx('position:absolute;top:-2px;bottom:-2px;width:2px;background:#22272E;left:' + ((sd.limitDays / mx) * 100).toFixed(1) + '%;')} />
                  </span>
                  <span style={sx('font-size:14px;font-weight:700;text-align:right;font-variant-numeric:tabular-nums;')}>{xs.length ? Math.round(avg) + 'd' : DASH}</span>
                  <span style={sx('font-size:14px;color:#54606C;')}>{sd.limitDays + 'd'}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:10px 2px;min-width:0;')}>
          <div style={sx(CAPS + 'margin-bottom:5px;')}>Stage moves · Truth Ledger</div>
          {b.moves.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:4px 0;')}>No move logged yet.</div>}
          {b.moves.slice(0, 4).map((m, i) => (
            <div key={i} style={sx('display:flex;align-items:baseline;gap:10px;padding:4px 0;border-bottom:1px solid rgba(34,39,46,.06);font-size:14px;')}>
              <span style={sx('flex:0 0 96px;color:#54606C;font-variant-numeric:tabular-nums;')}>{moveTime(m.at, now)}</span>
              <span title={m.what} style={sx('flex:1;min-width:0;color:#22272E;overflow-wrap:break-word;')}>{m.what}</span>
              <span style={sx('color:#54606C;')}>{m.who}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
